/**
 * AttendeesInput - Multi-select input for adding attendees (users & contacts)
 * to a calendar event.
 *
 * Searches users and contacts via useEntitySearch. Attendees are stored as
 * email addresses (matching the CalendarEventCreateParams schema).
 * Supports keyboard navigation, paste, and manual email entry.
 */

import type { ClipboardEvent, KeyboardEvent } from 'react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TagCloseX } from '@/lib/base/tags/tag-close-x';
import { EntityBadgeByEmail } from '@/lib/crm/components/badges/EntityBadgeByEmail';
import {
  type ContactResult,
  type EntityResult,
  useEntitySearch,
  type UserResult,
} from '@/lib/search';

import { AttendeesDropdown } from './AttendeesDropdown';
import {
  hasEmail,
  isValidEmail,
  MIN_QUERY_LENGTH,
  parseEmails,
  SEARCH_TYPES,
} from './attendeesUtils';

interface AttendeesInputProps {
  attendees: string[];
  onChange: (attendees: string[]) => void;
  orgId: number;
  disabled?: boolean;
}

export function AttendeesInput({
  attendees,
  onChange,
  orgId,
  disabled = false,
}: AttendeesInputProps) {
  const instanceId = useId();
  const inputId = `${instanceId}-input`;
  const listboxId = `${instanceId}-listbox`;
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchEnabled = inputValue.length >= MIN_QUERY_LENGTH && !disabled;
  const {
    results,
    isLoading,
    error: searchError,
  } = useEntitySearch(inputValue, {
    types: SEARCH_TYPES,
    enabled: searchEnabled,
  });

  const attendeesLower = useMemo(
    () => new Set(attendees.map(e => e.toLowerCase())),
    [attendees]
  );

  const flatResults = useMemo((): (UserResult | ContactResult)[] => {
    if (!results) {
      return [];
    }
    const all: EntityResult[] = [
      ...(results.users || []),
      ...(results.contacts || []),
    ];
    return all.filter(
      (entity): entity is UserResult | ContactResult =>
        hasEmail(entity) && !attendeesLower.has(entity.email.toLowerCase())
    );
  }, [results, attendeesLower]);

  const shouldShowDropdown =
    isDropdownOpen &&
    inputValue.length >= MIN_QUERY_LENGTH &&
    !searchError &&
    (isLoading || flatResults.length > 0 || (!isLoading && results !== null));

  useEffect(() => {
    setHighlightedIndex(flatResults.length > 0 ? 0 : -1);
  }, [flatResults.length]);

  const handleCloseDropdown = useCallback(() => setIsDropdownOpen(false), []);

  const handleSelectEntity = useCallback(
    (entity: UserResult | ContactResult) => {
      if (!attendeesLower.has(entity.email.toLowerCase())) {
        onChange([...attendees, entity.email]);
      }
      setInputValue('');
      setIsDropdownOpen(false);
      setError(null);
      inputRef.current?.focus();
    },
    [attendees, attendeesLower, onChange]
  );

  const addEmail = useCallback(
    (email: string) => {
      const trimmed = email.trim();
      if (!trimmed) {
        return;
      }
      if (!isValidEmail(trimmed)) {
        setError('Invalid email format');
        return;
      }
      if (attendeesLower.has(trimmed.toLowerCase())) {
        setError('Already added');
        return;
      }
      onChange([...attendees, trimmed]);
      setInputValue('');
      setError(null);
    },
    [attendees, attendeesLower, onChange]
  );

  const removeAttendee = useCallback(
    (index: number) => {
      onChange(attendees.filter((_, i) => i !== index));
    },
    [attendees, onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (shouldShowDropdown && flatResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev < flatResults.length - 1 ? prev + 1 : 0
          );
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : flatResults.length - 1
          );
          return;
        }
        if (e.key === 'Enter' && highlightedIndex >= 0) {
          e.preventDefault();
          handleSelectEntity(flatResults[highlightedIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setIsDropdownOpen(false);
          return;
        }
      }

      if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
        e.preventDefault();
        addEmail(inputValue);
        return;
      }

      if (e.key === 'Backspace' && inputValue === '' && attendees.length > 0) {
        removeAttendee(attendees.length - 1);
      }
    },
    [
      shouldShowDropdown,
      flatResults,
      highlightedIndex,
      handleSelectEntity,
      addEmail,
      inputValue,
      attendees.length,
      removeAttendee,
    ]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      const emails = parseEmails(pastedText);
      if (emails.length >= 1) {
        e.preventDefault();
        const seen = new Set(attendeesLower);
        const valid = emails.filter(email => {
          const lower = email.toLowerCase();
          if (!isValidEmail(email) || seen.has(lower)) {
            return false;
          }
          seen.add(lower);
          return true;
        });
        if (valid.length > 0) {
          onChange([...attendees, ...valid]);
          setInputValue('');
          setError(null);
        }
      }
    },
    [attendees, attendeesLower, onChange]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setError(null);
    setIsDropdownOpen(value.length >= MIN_QUERY_LENGTH);
  }, []);

  const handleInputFocus = useCallback(
    () => setIsDropdownOpen(inputValue.length >= MIN_QUERY_LENGTH),
    [inputValue.length]
  );

  const handleContainerClick = useCallback(() => inputRef.current?.focus(), []);

  const handleInputChangeEvent = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      handleInputChange(e.target.value),
    [handleInputChange]
  );

  return (
    <div className='space-y-1'>
      <label htmlFor={inputId} className='text-xs font-medium text-fg-tertiary'>
        Attendees
      </label>
      <div className='relative' ref={containerRef}>
        <div
          className='flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-transparent border-b border-border-secondary focus-within:border-brand-500 transition-colors cursor-text'
          onClick={handleContainerClick}
        >
          {attendees.map((email, index) => (
            <span
              key={email}
              className='inline-flex items-center gap-1 max-w-full'
            >
              <EntityBadgeByEmail
                email={email}
                orgId={orgId}
                showEmail
                interactive={false}
                clickBehavior='none'
              />
              {!disabled && (
                <TagCloseX
                  size='sm'
                  onPress={() => removeAttendee(index)}
                  className='cursor-pointer'
                />
              )}
            </span>
          ))}
          <input
            ref={inputRef}
            id={inputId}
            type='text'
            value={inputValue}
            onChange={handleInputChangeEvent}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={handleInputFocus}
            placeholder={
              attendees.length === 0 ? 'Search users or contacts' : ''
            }
            disabled={disabled}
            className='flex-1 min-w-[120px] outline-none bg-transparent text-sm text-fg-primary placeholder:text-fg-tertiary'
            role='combobox'
            aria-label='Attendees'
            aria-expanded={shouldShowDropdown}
            aria-controls={listboxId}
            aria-activedescendant={
              shouldShowDropdown && highlightedIndex >= 0
                ? `${instanceId}-option-${highlightedIndex}`
                : undefined
            }
            autoComplete='off'
          />
        </div>

        {shouldShowDropdown && (
          <AttendeesDropdown
            suggestions={flatResults}
            highlightedIndex={highlightedIndex}
            onHighlightChange={setHighlightedIndex}
            onSelect={handleSelectEntity}
            isLoading={isLoading}
            hasFinishedSearching={!isLoading && results !== null}
            instanceId={instanceId}
            listboxId={listboxId}
            containerRef={containerRef}
            onClose={handleCloseDropdown}
          />
        )}
      </div>
      {error && <span className='text-xs text-utility-error-600'>{error}</span>}
    </div>
  );
}
